<script>
    import { toast } from "@zerodevx/svelte-toast";
    import { v4 as uuidv4 } from "uuid";
    import Papa from "papaparse";
    import { saveAs } from "file-saver";
    import { Grid, Willow } from "@svar-ui/svelte-grid";
    import { diff } from "deep-object-diff";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from "@app/paraglide/messages.js";
    import {
        knpState,
        saveKnpTerm,
        deleteKnpTerm,
        batchSaveTerms,
    } from "@app/store/knpStore.svelte.js";

    let termSearchQuery = $state("");
    let api = $state();
    let originalGridData = $state([]);
    let selectedRows = $state([]);
    let history = $derived(api?.getReactiveState()?.history);

    // Datagrid columns
    let gridColumns = $derived.by(() => {
        if (!knpState.selectedGuide) return [];
        
        const columns = [
            { 
                id: "term", 
                header: m.knp_terms_column_term(), 
                width: 200,
                editor: "text"
            },
            {
                id: "type",
                header: m.knp_terms_column_type(),
                width: 150,
                editor: "richselect",
                options: [
                    { id: "character", label: (m.knp_terms_type_character()) },
                    { id: "phrase", label: (m.knp_terms_type_phrase()) },
                    { id: "location", label: (m.knp_terms_type_location()) },
                    { id: "organization", label: (m.knp_terms_type_organization()) },
                    { id: "title", label: (m.knp_terms_type_title()) }
                ]
            }
        ];

        // Add columns for each language
        knpState.selectedGuide.languages?.forEach((lang) => {
            columns.push({
                id: lang,
                header: lang.toUpperCase(),
                width: 200,
                editor: "text"
            });
        });

        return columns;
    });

    // Datagrid rows
    let gridRows = $derived.by(() => {
        let filteredTerms = knpState.terms;
        
        if (termSearchQuery.trim()) {
            const query = termSearchQuery.toLowerCase();
            filteredTerms = filteredTerms.filter((term) => {
                if (term.term.toLowerCase().includes(query)) return true;
                
                if (term.translations) {
                    for (let lang in term.translations) {
                        if (term.translations[lang].toLowerCase().includes(query)) {
                            return true;
                        }
                    }
                }
                
                return false;
            });
        }
        
        return filteredTerms.map(term => ({
            id: term.id,
            term: term.term,
            type: term.type,
            ...(term.translations || {})
        }));
    });

    // Update originalGridData when terms change
    $effect(() => {
        if (!termSearchQuery.trim() && knpState.terms.length > 0) {
            const transformedData = knpState.terms.map(term => ({
                id: term.id,
                term: term.term,
                type: term.type,
                ...(term.translations || {})
            }));
            originalGridData = JSON.parse(JSON.stringify(transformedData));
        }
    });

    function addNewTerm() {
        if (!knpState.selectedGuide) return;

        const newTerm = {
            id: uuidv4(),
            term: m.knp_terms_new_term(),
            type: "phrase",
            translations: {}
        };

        knpState.selectedGuide.languages?.forEach((lang) => {
            newTerm.translations[lang] = "";
        });

        saveKnpTerm(newTerm)
            .then(() => {
                toast.push(m.knp_terms_new_term_added(), {
                    classes: ["toast-success"],
                });
            })
            .catch((error) => {
                console.error("Error adding term:", error);
                toast.push(m.knp_terms_add_term_failed({ error: error.message }), {
                    classes: ["toast-danger"],
                });
            });
    }

    async function handleSaveChanges() {
        if (!api) {
            toast.push(m.knp_terms_grid_not_initialized(), {
                classes: ["toast-danger"],
            });
            return;
        }

        // Commit any in-progress cell edit before reading grid data
        const { editor } = api.getState();
        if (editor) {
            api.exec("close-editor", { ignore: false });
        }

        const { data: currentData } = api.getState();
        const originalSnapshot = $state.snapshot(originalGridData);

        const originalMap = new Map(originalSnapshot.map(row => [row.id, row]));
        const currentMap = new Map(currentData.map(row => [row.id, row]));
        
        const toUpdate = [];
        const toDelete = [];
        const toCreate = [];
        
        for (const currentRow of currentData) {
            const originalRow = originalMap.get(currentRow.id);
            
            if (!originalRow) {
                toCreate.push(currentRow);
            } else {
                const differences = diff(originalRow, currentRow);
                
                if (Object.keys(differences).length > 0) {
                    toUpdate.push(currentRow);
                }
            }
        }
        
        for (const originalRow of originalSnapshot) {
            if (!currentMap.has(originalRow.id)) {
                toDelete.push(originalRow.id);
            }
        }
        
        if (toUpdate.length === 0 && toDelete.length === 0 && toCreate.length === 0) {
            toast.push(m.knp_terms_no_changes_to_save(), {
                classes: ["toast-info"],
            });
            return;
        }
        
        const savePromises = [];
        
        for (const row of toUpdate) {
            const term = knpState.terms.find(t => t.id === row.id);
            if (!term) continue;
            
            term.term = row.term;
            term.type = row.type;
            
            if (!term.translations) term.translations = {};
            knpState.selectedGuide.languages?.forEach((lang) => {
                if (row[lang] !== undefined) {
                    term.translations[lang] = row[lang];
                }
            });
            
            savePromises.push(saveKnpTerm(term));
        }
        
        for (const row of toCreate) {
            const newTerm = {
                id: row.id,
                term: row.term,
                type: row.type,
                translations: {}
            };
            
            knpState.selectedGuide.languages?.forEach((lang) => {
                if (row[lang] !== undefined) {
                    newTerm.translations[lang] = row[lang];
                }
            });
            
            savePromises.push(saveKnpTerm(newTerm));
        }
        
        try {
            await Promise.all(savePromises);
            originalGridData = JSON.parse(JSON.stringify(currentData));
            
            const totalChanges = toUpdate.length + toDelete.length + toCreate.length;
            toast.push(m.knp_terms_saved_changes_success({ count: totalChanges }), {
                classes: ["toast-success"],
            });
        } catch (error) {
            console.error("Error saving changes:", error);
            toast.push(m.knp_terms_save_changes_failed({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }

    async function resetGrid() {
        if (!api) return;
        
        const resetData = JSON.parse(JSON.stringify(originalGridData));
        api.setNext({ data: resetData });
        
        toast.push(m.knp_terms_changes_discarded(), {
            classes: ["toast-info"],
        });
    }

    function handleUndo() {
        api?.exec("undo");
    }

    function handleRedo() {
        api?.exec("redo");
    }

    async function handleDeleteSelected() {
        let selectedRows = api.getState().selectedRows;
        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.destructive,
            title: m.knp_terms_delete_terms_title(),
            text: m.knp_terms_delete_terms_text({ count: selectedRows.length }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.delete(),
            cancelButtonText: m.cancel(),
        });

        if (result.isConfirmed) {
            const deletePromises = selectedRows.map(id => deleteKnpTerm(id));
            
            try {
                await Promise.all(deletePromises);
                toast.push(m.knp_terms_deleted_terms_success({ count: selectedRows.length }), {
                    classes: ["toast-success"],
                });
                selectedRows = [];
            } catch (error) {
                console.error("Error deleting terms:", error);
                toast.push(m.knp_terms_delete_terms_failed({ error: error.message }), {
                    classes: ["toast-danger"],
                });
            }
        }
    }

    function handleExportCSV() {
        if (!knpState.selectedGuide || knpState.terms.length === 0) {
            toast.push(m.knp_terms_no_terms_to_export(), {
                classes: ["toast-warning"],
            });
            return;
        }

        const csvData = knpState.terms.map((term) => {
            const row = {
                id: term.id,
                term: term.term,
                type: term.type
            };

            knpState.selectedGuide.languages?.forEach((lang) => {
                row[lang] = term.translations?.[lang] || "";
            });

            return row;
        });

        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, `${knpState.selectedGuide.title}_terms.csv`);

        toast.push(m.knp_terms_exported_csv(), {
            classes: ["toast-success"],
        });
    }

    function handleImportCSV() {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".csv";

        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;

            Papa.parse(file, {
                header: true,
                complete: (results) => {
                    const importedTerms = [];
                    const errors = [];

                    results.data.forEach((row, index) => {
                        if (!row.term || !row.term.trim()) {
                            errors.push(m.knp_terms_csv_row_missing_term({ row: index + 1 }));
                            return;
                        }

                        const term = {
                            id: row.id || uuidv4(),
                            term: row.term,
                            type: row.type || "phrase",
                            translations: {}
                        };

                        knpState.selectedGuide.languages?.forEach((lang) => {
                            term.translations[lang] = row[lang] || "";
                        });

                        importedTerms.push(term);
                    });

                    if (errors.length > 0) {
                        console.warn("CSV import errors:", errors);
                    }

                    if (importedTerms.length > 0) {
                        batchSaveTerms(importedTerms)
                            .then(() => {
                                toast.push(m.knp_terms_imported_terms({ count: importedTerms.length }), {
                                    classes: ["toast-success"],
                                });

                                if (errors.length > 0) {
                                    toast.push(m.knp_terms_rows_with_errors({ count: errors.length }), {
                                        classes: ["toast-warning"],
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error("Error importing terms:", error);
                                toast.push(m.knp_terms_import_terms_failed({ error: error.message }), {
                                    classes: ["toast-danger"],
                                });
                            });
                    } else {
                        toast.push(m.knp_terms_no_valid_terms(), {
                            classes: ["toast-warning"],
                        });
                    }
                },
                error: (error) => {
                    console.error("CSV parse error:", error);
                    toast.push(m.knp_terms_parse_csv_failed({ error: error.message }), {
                        classes: ["toast-danger"],
                    });
                }
            });
        };

        input.click();
    }
</script>

<style>
    :global(.knp-datagrid-container) {
        border: 1px solid #dee2e6;
        border-radius: 0.375rem;
        overflow: hidden;
    }
</style>

<div class="tab-pane active">
    <!-- Terms Toolbar -->
    <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="d-flex gap-2 align-items-center">
            <div class="input-group" style="max-width: 300px;">
                <span class="input-group-text">
                    <i class="bi bi-search"></i>
                </span>
                <input
                    type="text"
                    class="form-control"
                    placeholder={m.knp_terms_search_placeholder()}
                    bind:value={termSearchQuery}
                />
            </div>
            
            <!-- Add/Delete Group -->
            <div class="btn-group">
                <button
                    class="btn btn-sm btn-success"
                    onclick={addNewTerm}
                    title={m.knp_terms_add_new_term_title()}
                >
                    <i class="bi bi-plus-lg"></i>
                </button>
                <button
                    class="btn btn-sm btn-danger"
                    onclick={handleDeleteSelected}
                    title={m.knp_terms_delete_selected_terms_title()}
                >
                    <i class="bi bi-trash"></i>
                </button>
            </div>

            <!-- Undo/Redo Group -->
            <div class="btn-group">
                <button
                    class="btn btn-sm btn-outline-secondary"
                    onclick={handleUndo}
                    disabled={history && !history?.undo}
                    title={m.knp_terms_undo()}
                >
                    <i class="bi bi-arrow-counterclockwise"></i>
                </button>
                <button
                    class="btn btn-sm btn-outline-secondary"
                    onclick={handleRedo}
                    disabled={history && !history?.redo}
                    title={m.knp_terms_redo()}
                >
                    <i class="bi bi-arrow-clockwise"></i>
                </button>
            </div>
        </div>

        <!-- Save Changes Button (Prominent) -->
        <button 
            class="btn btn-primary"
            onclick={() => handleSaveChanges()}
            title={m.save_changes()}
        >
            <i class="bi bi-floppy me-2"></i> {m.save()}
        </button>
    </div>

    <!-- Terms Datagrid -->
    {#if knpState.loading}
        <div class="text-center py-5">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">{m.knp_terms_loading()}</span>
            </div>
        </div>
    {:else if knpState.terms.length === 0}
        <div class="alert alert-info" role="alert">
            {m.knp_terms_empty_state()}
        </div>
    {:else}
        <div class="knp-datagrid-container" style="height: 75svh;">
            <Willow>
                <Grid
                    data={gridRows}
                    columns={gridColumns}
                    bind:this={api}
                    selection={true}
                    undo={true}
                />
            </Willow>
        </div>
        <div class="d-flex justify-content-between align-items-center mt-2">
            <p class="small text-muted mb-0">
                <i class="bi bi-info-circle"></i> {m.knp_terms_edit_hint()}
            </p>
            <div class="btn-group btn-group-sm">
                <button
                    class="btn btn-outline-secondary"
                    onclick={handleImportCSV}
                    title={m.knp_terms_import_csv_title()}
                >
                    <i class="bi bi-upload"></i> {m.knp_terms_import_csv()}
                </button>
                <button
                    class="btn btn-outline-secondary"
                    onclick={handleExportCSV}
                    title={m.knp_terms_export_csv_title()}
                >
                    <i class="bi bi-download"></i> {m.knp_terms_export_csv()}
                </button>
            </div>
        </div>
    {/if}
</div>
